import "@/App.css";
import { useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { DateRangeProvider } from "@/components/DateRangeFilter";
import LoginPage from "@/pages/LoginPage";
import CompanyOverview from "@/pages/CompanyOverview";
import Dashboard from "@/pages/Dashboard";
import CohortAnalysis from "@/pages/CohortAnalysis";
import LTVAnalysis from "@/pages/LTVAnalysis";
import CategoryAnalysis from "@/pages/CategoryAnalysis";
import RevenueExpansion from "@/pages/RevenueExpansion";
import GeographyPage from "@/pages/Geography";
import AdsTraffic from "@/pages/AdsTraffic";
import ExportPDF from "@/pages/ExportPDF";
import FinancialOverview from "@/pages/FinancialOverview";
import ChannelPnL from "@/pages/ChannelPnL";
import RevenueCuts from "@/pages/RevenueCuts";
import Projections from "@/pages/Projections";
import FARPage from "@/pages/FARPage";
import SalesAnalysis from "@/pages/SalesAnalysis";
import FinancialExport from "@/pages/FinancialExport";
import AIChat from "@/pages/AIChat";
import UserManagement from "@/pages/UserManagement";

function App() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("token"));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("user") || "null"); } catch { return null; }
  });

  const handleLogin = useCallback((userData, token) => {
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    setAuthed(true);
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.clear();
    setUser(null);
    setAuthed(false);
  }, []);

  if (!authed) return <LoginPage onLogin={handleLogin} />;

  const perms = user?.permissions || [];
  const hasCohort = perms.includes("cohort") || perms.includes("admin");
  const hasFinancial = perms.includes("financial") || perms.includes("admin");
  const isAdmin = user?.role === "super_admin";

  return (
    <BrowserRouter>
      <DateRangeProvider>
        <div className="noise-overlay" />
        <Routes>
          <Route element={<Layout user={user} onLogout={handleLogout} />}>
            <Route path="/" element={<CompanyOverview />} />
            {/* Cohort */}
            {hasCohort && <>
              <Route path="/cohort/dashboard" element={<Dashboard />} />
              <Route path="/cohort/analysis" element={<CohortAnalysis />} />
              <Route path="/cohort/ltv" element={<LTVAnalysis />} />
              <Route path="/cohort/category" element={<CategoryAnalysis />} />
              <Route path="/cohort/revenue" element={<RevenueExpansion />} />
              <Route path="/cohort/geography" element={<GeographyPage />} />
              <Route path="/cohort/ads" element={<AdsTraffic />} />
              <Route path="/cohort/export" element={<ExportPDF />} />
            </>}
            {/* Financial */}
            {hasFinancial && <>
              <Route path="/financial/pnl" element={<FinancialOverview />} />
              <Route path="/financial/channels" element={<ChannelPnL />} />
              <Route path="/financial/revenue" element={<RevenueCuts />} />
              <Route path="/financial/projections" element={<Projections />} />
              <Route path="/financial/far" element={<FARPage />} />
              <Route path="/financial/sales" element={<SalesAnalysis />} />
              <Route path="/financial/export" element={<FinancialExport />} />
            </>}
            {/* Tools */}
            <Route path="/insights" element={<AIChat />} />
            {isAdmin && <Route path="/admin/users" element={<UserManagement />} />}
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </DateRangeProvider>
    </BrowserRouter>
  );
}

export default App;
